import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../../shared/guards/ProfileGuard";
import { useDebounce, useToast } from "../../../../shared/hooks";
import { ComissionServices } from "../../../../shared/services/api/comission/ComissionServices";
import { useProfile } from "../../../../context/ProfileContext";
import { ModalConfirmSelect } from "../Modals/ModalConfirmSelect";
import { TableComission } from "../TableComission";
import { ComissionToolbar } from "../ToolbarCommission";

export const TabComission = () => {
	const { debounce } = useDebounce();
	const toastHandler = useToast();
	const { isSuperAdmin } = useProfile();
	// Ação granular Livve — pedido formalizado pro backend, ver GAPS_LIVVE_V2_PARA_BACKEND.md.
	const canCloseCycle = isSuperAdmin || checkByProfileGuard(PermissionRolesEnum.FinancialOptions, PermissionActionsEnum.LivveClose);
	const [textSearch, setTextSearch] = useState("");
	const [dataIni, setDataIni] = useState("");
	const [dataFim, setDataFim] = useState("");
	const [status, setStatus] = useState("todos");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [refreshTrigger, setRefreshTrigger] = useState(0);
	const [comissionData, setComissionData] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
	const [isCloseCycleConfirmOpen, setIsCloseCycleConfirmOpen] = useState(false);
	const [isClosingCycle, setIsClosingCycle] = useState(false);

	const [clientId, setClientId] = useState("");

	// Função para tratar o status (evitar enviar "todos" como valor)
	const normalizeStatus = (raw: string) => (raw === "todos" ? "" : raw);

	const handleFetchData = (currentPage: number, limit: number, search: string, ini: string, fim: string, stat: string, clientId: string) => {
		setIsLoading(true);
		const finalStatus = normalizeStatus(stat);

		debounce(() => {
			ComissionServices.getComission(currentPage + 1, limit, search, ini, fim, finalStatus, clientId)
				.then((response) => {
					if (response instanceof Error) {
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: <ToastContent title="Erro" content="Erro ao buscar comissões" />,
						});
					} else {
						setComissionData(response.data);
						setTotalCount(response.totalCount ?? 0);
					}
				})
				.finally(() => setIsLoading(false));
		});
	};

	useEffect(() => {
		const defaultDataIni = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0];
		const defaultDataFim = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split("T")[0];

		setTextSearch("");
		setDataIni(defaultDataIni);
		setDataFim(defaultDataFim);
		setStatus("todos");
		setClientId("");
		setPage(0);
		setSelectedClientIds([]);
		console.log(defaultDataIni, defaultDataFim);

		handleFetchData(0, rowsPerPage, "", defaultDataIni, defaultDataFim, "todos", "");
	}, [refreshTrigger]);

	const handleSearch = () => {
		setPage(0);
		handleFetchData(0, rowsPerPage, textSearch, dataIni, dataFim, status, clientId);
	};

	const handleChangePage = (newPage: number) => {
		setPage(newPage);
		handleFetchData(newPage, rowsPerPage, textSearch, dataIni, dataFim, status, clientId);
	};

	const handleChangeRowsPerPage = (newRowsPerPage: number) => {
		setRowsPerPage(newRowsPerPage);
		setPage(0);
		handleFetchData(0, newRowsPerPage, textSearch, dataIni, dataFim, status, clientId);
	};

	const handleRefresh = () => setRefreshTrigger((prev) => prev + 1);

	const toggleSelect = (clientId: string) => {
		setSelectedClientIds((prev) => (prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId]));
	};

	const toggleSelectAll = () => {
		if (selectedClientIds.length === comissionData.length) {
			setSelectedClientIds([]);
		} else {
			setSelectedClientIds(comissionData.map((r) => r.clientId));
		}
	};

	const handleConfirmCloseCycle = async () => {
		setIsClosingCycle(true);
		try {
			const result = await ComissionServices.closeCycle({ clientIds: selectedClientIds });
			if (result instanceof Error) {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro" content={result.message} />,
				});
				return;
			}
			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Ciclo fechado" content={result.summary} />,
			});
			setIsCloseCycleConfirmOpen(false);
			setSelectedClientIds([]);
			handleRefresh();
		} finally {
			setIsClosingCycle(false);
		}
	};

	return (
		<Box>
			<ComissionToolbar
				showInputSearch
				showDateRange
				showStatusSelect
				textSearch={textSearch}
				onChangeTextSearch={setTextSearch}
				onClearTextSearch={() => setTextSearch("")}
				onChangeDateStart={setDataIni}
				onChangeDateEnd={setDataFim}
				onChangeStatus={setStatus}
				onClickRefresh={handleSearch}
				showClientSelect={false}
				onChangeClient={(id) => setClientId(id || "")}
				showCloseCycleButton={canCloseCycle && selectedClientIds.length > 0}
				onClickCloseCycle={() => setIsCloseCycleConfirmOpen(true)}
			/>

			<LoadingComp isopen={isLoading} />

			<TableComission
				rows={comissionData}
				page={page}
				rowsPerPage={rowsPerPage}
				totalCount={totalCount}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				dataIni={dataIni}
				dataFim={dataFim}
				onRefresh={handleRefresh}
				selectedClientIds={selectedClientIds}
				onToggleSelect={toggleSelect}
				onToggleSelectAll={toggleSelectAll}
			/>

			<ModalConfirmSelect
				open={isCloseCycleConfirmOpen}
				onClose={() => setIsCloseCycleConfirmOpen(false)}
				onConfirm={handleConfirmCloseCycle}
				title={`Fechar ciclo de ${selectedClientIds.length} imobiliária(s)`}
				description="Vai apurar todas as parcelas previstas dessas imobiliárias: quem bateu a meta do plano vira Aberto, quem não bateu vira Não elegível. Essa decisão não é reavaliada depois. Pra escolher parcelas específicas de uma imobiliária, use o menu da linha em vez do fechamento em lote."
				confirmLabel="Sim, fechar ciclo"
				cancelLabel="Cancelar"
				isLoading={isClosingCycle}
			/>
		</Box>
	);
};
